import * as _ from "lodash";
import * as Constants from "../common/constants";
import { createWebServiceDataSource } from "../common/data";
import * as Global from "../common/global";
import * as GridUtil from "../common/gridFilter";
import * as UI from "../common/ui";
import * as TargetData from "../data/targetData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

// classes used to display a list of targets
// parameters for filtering the list
interface ITargetListFilter {
    TargetTemplateURLName: string;
    SearchText?: string;
    IncludeActive: boolean;
    IncludeInactive: boolean;
}

export class TargetsController implements IController {
    // private properties

    // controls
    private grid: kendo.ui.Grid;
    private dataSource: kendo.data.DataSource;
    private gridFilterControl: GridUtil.GridFilterControl;

    // references to DOM elements
    private container: JQuery;
    private gridContainer: JQuery;

    // unique URL for this target
    private targetTemplateURLName: string = "";
    // user read-able name to display on the page
    private targetName: string = "";

    // public methods

    public render(container: JQuery): JQueryPromise<any> {
        const me: TargetsController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        me.container = container;
        me.gridContainer = container.find("div[data-id='grid']");

        deferred.resolve();

        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const app: IApplication = Global.app();
        const me: TargetsController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        if (params == null || params.length !== 1) {
            // invalid parameters passed into this page
            app.pageManager().closeCurrentPage();
            deferred.resolve();
            return deferred.promise();
        }

        this.targetTemplateURLName = params[0];

        TargetData.GetTargetsConfig(this.targetTemplateURLName)
            .done((response: Data.IResponse<TargetData.ITargetListConfigResponse>) => {
                if (response.Success) {
                    me.targetName = response.Payload.TargetName;
                    me.createGridControl(response.Payload.ResultScheme);
                    me.createGridFilterControl();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    public add(): void {
        Global.app().router().navigate("/Target/" + this.targetTemplateURLName, true);
    }

    public haveFieldsChanged(): boolean {
        // no editable fields
        return false;
    }

    public height(height: number): void {
        if (height > GridUtil.GRIDFILTERCONTROL_HEIGHT) {
            UI.resizeGrid(this.gridContainer, height - GridUtil.GRIDFILTERCONTROL_HEIGHT);
        }
    }

    // called when loaded page is redisplayed
    public refresh(): void {
        this.grid.dataSource.read();
    }

    // returns a unique ID for the control, so it can be redisplayed if
    // already open.  Used by the AppRouter class.
    public appRouterID(params: string[]): string {
        let targetName: string = "UNKNOWN";
        if (params.length > 0) {
            targetName = params[0];
        }
        return "Targets" + targetName;
    }

    // returns a title for the page
    public title(): string {
        return "Targets";
    }

    public subTitle(): string {
        return this.targetName;
    }

    private setDataFilter(data: ITargetListFilter): void {
        data.TargetTemplateURLName = this.targetTemplateURLName;
        data.IncludeActive = true;
        data.IncludeInactive = false;

        if (this.gridFilterControl != null) {
            this.gridFilterControl.setDataFilter(data);
        }
    }

    private createGridControl(resultScheme: GridUtil.IResultScheme): void {
        const me: TargetsController = this;
        const app: IApplication = Global.app();

        // for each column that is a link to the details, use a template
        // to render the column.
        _.forEach(resultScheme.Columns, (column: GridUtil.IResultSchemeColumn) => {
            if (column.IsLinkToDetail) {
                // get the template and replace the FIELDNAME place holder
                // with the real name of the field
                column.TemplateHTML = $("#template_targets_targetname").html()
                    .replace(/FIELDNAME/g, column.Field)
                    .replace(/TARGETURL/g, me.targetTemplateURLName);
            }
        });

        this.dataSource = createWebServiceDataSource(
            "/api/Targets",
            GridUtil.createDataSourceModel(resultScheme),
            (data: any) => {
                me.setDataFilter(data);
            },
            (status: number) => {
                if (status === Constants.HTTP_STATUS_UNAUTHORIZED) {
                    // user is not allowed to view this data, so close the page
                    app.pageManager().closeCurrentPage();
                }
                // for other errors, keep page open, allow user to retry.
            });

        this.grid = this.gridContainer.kendoGrid({
            dataSource: this.dataSource,
            selectable: false,
            sortable: true,
            filterable: true,
            pageable: true,
            columns: GridUtil.createColumnsModel(resultScheme),
            excel: {
                fileName: me.targetTemplateURLName + ".xlsx",
                filterable: true,
                allPages: true,
            },
        }).data("kendoGrid");

        // bind to all link tags in the grid
        me.gridContainer.on("click", "a[data-id='link']", function(e: any): boolean {
            const path: string | undefined = $(this).attr("href");
            e.preventDefault();
            // open the page
            app.router().navigate(_.defaultTo<string>(path, ""), true);
            return false;
        });
    }

    private importTargets(): void {
        Global.app().router().navigate(`/TargetImport/${this.targetTemplateURLName}`, true);
    }

    private createGridFilterControl(): void {
        const me: TargetsController = this;

        me.gridFilterControl = new GridUtil.GridFilterControl(me.container.find("div[data-id='filter']"), {
            showTextSearch: true,
            showExportToExcel: true,
            showFilters: true,
            showRefresh: true,
            filters: [
                { name: Constants.PHRASE_FILTER_SHOWACTIVE, checked: true },
                { name: Constants.PHRASE_FILTER_SHOWINACTIVE, checked: false },
            ],
            grid: me.grid,
            buttons: [{ name: "Import Targets", iconClass: "fa fa-upload", onClick: this.importTargets.bind(me) }],
        });
    }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/targets.ts